import { useCallback, useRef, useState, type RefObject } from 'react';
import type { BrainStatus, BrainStreamSnapshotFrame, BrainUsage } from '../../lib/types';

interface BrainChatUsageOptions {
  generationRef: RefObject<number>;
  sessionRef: RefObject<string | undefined>;
  readStatus: (session?: string) => Promise<BrainStatus>;
}

interface UsageReadFence {
  stamp: number;
  readId: number;
  generation: number;
  session: string | undefined;
}

/** A reconnect snapshot replays its tail in daemon order. Usage is cumulative within that order, so the
 *  final usage-bearing step/idle is the authoritative value at the snapshot boundary. */
export function snapshotUsage(events: BrainStreamSnapshotFrame['events']): BrainUsage | undefined {
  let latest: BrainUsage | undefined;
  for (const event of events) {
    if ((event.type === 'step' || event.type === 'idle') && event.usage && typeof event.usage === 'object') {
      latest = event.usage as BrainUsage;
    }
  }
  return latest;
}
/**
 * One usage authority for stream events and REST samples. Stream boundaries supersede pending reads;
 * among REST reads, only the latest-started sample may publish into the same session and generation.
 */
export function useBrainChatUsage({
  generationRef, sessionRef, readStatus,
}: BrainChatUsageOptions) {
  const [usage, setUsageState] = useState<BrainUsage | null>(null);
  const usageStampRef = useRef(0);
  const usageReadIdRef = useRef(0);
  const fenceUsage = useCallback((): number => (usageStampRef.current += 1), []);
  const startUsageRead = useCallback((session = sessionRef.current): UsageReadFence => ({
    stamp: usageStampRef.current,
    readId: usageReadIdRef.current += 1,
    generation: generationRef.current,
    session,
  }), [generationRef, sessionRef]);
  const setUsage = useCallback((next: BrainUsage | null): void => {
    fenceUsage();
    setUsageState(next);
  }, [fenceUsage]);
  const setUsageIfFresh = useCallback((next: BrainUsage | null, fence: UsageReadFence): void => {
    if (fence.stamp === usageStampRef.current && fence.readId === usageReadIdRef.current
      && fence.generation === generationRef.current && fence.session === sessionRef.current) {
      setUsageState(next);
    }
  }, [generationRef, sessionRef]);
  const applyUsageSnapshot = (events: BrainStreamSnapshotFrame['events']): boolean => {
    fenceUsage();
    const replayed = snapshotUsage(events);
    if (replayed) setUsageState(replayed);
    return replayed !== undefined;
  };

  // Stream steps and idle carry usage; snapshots and explicit state changes own REST refreshes.
  // Keep their reader current without a second polling owner beside the live stream.
  const readStatusRef = useRef(readStatus);
  readStatusRef.current = readStatus;
  const refreshUsage = useCallback(async (session = sessionRef.current): Promise<void> => {
    const fence = startUsageRead(session);
    const status = await readStatusRef.current(session);
    setUsageIfFresh(status.usage, fence);
  }, [sessionRef, startUsageRead, setUsageIfFresh]);

  return { usage, fenceUsage, startUsageRead, setUsage, setUsageIfFresh, applyUsageSnapshot, refreshUsage };
}
